/**
 * @fileoverview Workflow role icon registry
 *
 * The curated Lucide set a workflow role can be given. The keys are the closed
 * union declared as `workflowRoleIconValidator` in
 * `convex/tasks/workflowRoles/schema.ts`, so this
 * map is a typed `Record` — adding a key server-side without adding it here
 * fails the build, and vice versa.
 *
 * The picker component lives in `IconSwatchPicker.tsx`; this file stays
 * component-free so it can be imported from anywhere without tripping fast
 * refresh.
 */

import type { LucideIcon } from "lucide-react";
import {
  BadgeCheck,
  Banknote,
  BarChart3,
  Book,
  Briefcase,
  Building2,
  Calculator,
  ClipboardList,
  Compass,
  Crown,
  FileSearch,
  Flag,
  FolderOpen,
  Gavel,
  GraduationCap,
  Headset,
  Key,
  Landmark,
  Layers,
  Mail,
  PenLine,
  Phone,
  Scale,
  Search,
  Shield,
  Star,
  Target,
  UserCheck,
  Users,
  Wrench,
} from "lucide-react";
import type { WorkflowRoleIcon } from "@cvx/tasks/workflowRoles/schema";

/** Icon component and human label for every allowed key. */
export const ROLE_ICONS: Record<
  WorkflowRoleIcon,
  { Icon: LucideIcon; label: string }
> = {
  crown: { Icon: Crown, label: "Crown" },
  briefcase: { Icon: Briefcase, label: "Briefcase" },
  gavel: { Icon: Gavel, label: "Gavel" },
  scale: { Icon: Scale, label: "Scales" },
  shield: { Icon: Shield, label: "Shield" },
  search: { Icon: Search, label: "Search" },
  "file-search": { Icon: FileSearch, label: "File search" },
  pen: { Icon: PenLine, label: "Pen" },
  chart: { Icon: BarChart3, label: "Chart" },
  calculator: { Icon: Calculator, label: "Calculator" },
  landmark: { Icon: Landmark, label: "Landmark" },
  users: { Icon: Users, label: "People" },
  "user-check": { Icon: UserCheck, label: "Approver" },
  clipboard: { Icon: ClipboardList, label: "Clipboard" },
  phone: { Icon: Phone, label: "Phone" },
  mail: { Icon: Mail, label: "Mail" },
  book: { Icon: Book, label: "Book" },
  key: { Icon: Key, label: "Key" },
  flag: { Icon: Flag, label: "Flag" },
  star: { Icon: Star, label: "Star" },
  target: { Icon: Target, label: "Target" },
  wrench: { Icon: Wrench, label: "Wrench" },
  headset: { Icon: Headset, label: "Headset" },
  banknote: { Icon: Banknote, label: "Banknote" },
  "graduation-cap": { Icon: GraduationCap, label: "Graduation cap" },
  compass: { Icon: Compass, label: "Compass" },
  layers: { Icon: Layers, label: "Layers" },
  stamp: { Icon: BadgeCheck, label: "Certified" },
  folder: { Icon: FolderOpen, label: "Folder" },
  building: { Icon: Building2, label: "Building" },
};

/** Picker order — the map above is keyed for lookup, this drives the grid. */
export const ROLE_ICON_KEYS = Object.keys(ROLE_ICONS) as WorkflowRoleIcon[];

/**
 * Resolves a stored icon key to its component. Returns null for "no icon" and
 * for a key that is no longer in the set, so callers render the plain badge
 * rather than crash on stale data.
 */
export function roleIconComponent(
  icon: WorkflowRoleIcon | undefined | null,
): LucideIcon | null {
  if (!icon) return null;
  return ROLE_ICONS[icon]?.Icon ?? null;
}
